import React, { useEffect, useState } from 'react'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import RecordCountTime from './RecordCountTime'
import RecordAccessGeographic from './RecordAccessGeographic'
import '../../styles/overview.scss'
import FileCountGrowthGraph from './FileCountGrowthGraph'
import CountFileType from './CountFileType'
import RetentionPeriod from './RetentionPeriod'
import QuickAction from './QuickAction'
import {
  getAnalyticStat,
  getDeptUserWiseAnalytics,
  getRecordAccessRequest,
  getStorageUtilization,
} from '../../core/_requests'
import { useAuth } from '../../../auth'
import Skeleton from 'react-loading-skeleton'
import { useIntl } from 'react-intl'
import { recordsKeeperColors } from '../../core/_constants'
import { AnalyticsOverviewPieChartWidget } from './AnalyticsOverviewPieChartWidget'
import DepartmentWiseActivity from './DepartmentWiseActivity'
import { DepartmentWiseChartProps, RecordAccessRequestProps } from '../../core/_models'
import DepartmentActivity from './DepartmentActivity'

const AnalyticsOverview: React.FC = () => {
  // const {formatMessage} = useIntl()
  const { companyId } = useAuth()
  const [storageUtilizationData, setStorageUtilizationData] = useState([])
  const [loading, setLoading] = useState(false)
  const [analyticStatLoading, setAnalyticStatLoading] = useState(false)
  const [analyticStatData, setAnalyticStatData] = useState<any>({})
  const [deptSelectedOptions, setDeptSelectedOptions] = useState<any[]>([])
  const [userSelectedOptions, setUserSelectedOptions] = useState<any[]>([])
  const [userRecordAccessSelectedOptions, setUserRecordAccessSelectedOptions] = useState<any[]>([])
  const [userWiseAnalytics, setUserWiseAnalytics] = useState<DepartmentWiseChartProps>([
    {
      date: 'Mar 05',
      activities: {
        SHARED: 0,
        EDITED: 0,
        RENAMED: 0,
      },
    },
  ])

  const [deptWiseAnalytics, setDeptWiseAnalytics] = useState<DepartmentWiseChartProps>([
    {
      date: 'Mar 05',
      activities: {
        SHARED: 0,
        EDITED: 0,
        RENAMED: 0,
      },
    },
  ])
  const [deptWiseAnalyticsLoading, setDeptWiseAnalyticsLoading] = useState(false)
  const [userWiseAnalyticsLoading, setUserWiseAnalyticsLoading] = useState(false)
  const [recordAccessRequestLoading, setRecordAccessRequestLoading] = useState(false)
  const [recordAccessRequestData, setRecordAccessRequestData] = useState<RecordAccessRequestProps>({
    labels: [],
    users: [],
  })

  const [files, setFiles] = useState()
  const [user, setUser] = useState()
  const { formatMessage } = useIntl()

  const storageUtilization = async () => {
    if (!companyId) return
    try {
      setLoading(true)
      const {
        data: { success, data, errors },
      } = await getStorageUtilization(companyId)
      if (success) {
        setStorageUtilizationData(data)
      } else {
        console.error(errors)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchDeptWiseAnalytics = async () => {
    if (!companyId) return
    try {
      setDeptWiseAnalyticsLoading(true)
      const {
        data: { success, data, errors },
      } = await getDeptUserWiseAnalytics(companyId, 'dept', deptSelectedOptions)
      if (success) {
        setDeptWiseAnalytics(data.departmentWiseActivity)
      } else {
        console.error(errors)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setDeptWiseAnalyticsLoading(false)
    }
  }

  const fetchUserWiseAnalytics = async () => {
    if (!companyId) return
    try {
      setUserWiseAnalyticsLoading(true)
      const {
        data: { success, data, errors },
      } = await getDeptUserWiseAnalytics(companyId, 'user', userSelectedOptions)
      if (success) {
        setUserWiseAnalytics(data.userWiseActivity)
      } else {
        console.error(errors)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setUserWiseAnalyticsLoading(false)
    }
  }

  const analyticStat = async () => {
    if (!companyId) return
    try {
      setAnalyticStatLoading(true)
      const {
        data: { success, data, errors },
      } = await getAnalyticStat(companyId, files, user)
      if (success) {
        setAnalyticStatData(data)
      } else {
        console.error(errors)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setAnalyticStatLoading(false)
    }
  }

  const fetchRecordAccessRequest = async () => {
    if (!companyId) return
    try {
      setRecordAccessRequestLoading(true)
      const {
        data: { success, data, errors },
      } = await getRecordAccessRequest(companyId, userRecordAccessSelectedOptions)
      if (success) {
        setRecordAccessRequestData(data)
      } else {
        console.error(errors)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setRecordAccessRequestLoading(false)
    }
  }

  useEffect(() => {
    storageUtilization()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    analyticStat()
  }, [files, user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDeptWiseAnalytics()
  }, [deptSelectedOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUserWiseAnalytics()
  }, [userSelectedOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRecordAccessRequest()
  }, [userRecordAccessSelectedOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className='mt-4'>
        <InfoCard2
          title={'Gain Insights with Analytics & Reports'}
          desc={
            'This page provides you with detailed insights into your records activity, including access trends, user activity, storage utilization, and retention period statuses. Visualize key metrics to track document usage, compliance, and storage distribution. Use these analytics to optimize your records management strategy and generate reports for auditing and decision-making.'
          }
          slug={
            'https://help.recordskeeper.ai/en/articles/10373866-understanding-recordskeeper-ai-analytics-reports'
          }
        />
      </div>
      <div className='row'>
        <div className='mb-6 col-md-12 col-12'>
          {recordAccessRequestLoading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <DepartmentActivity
              recordAccessRequestData={recordAccessRequestData}
              selectedOptions={userRecordAccessSelectedOptions}
              setSelectedOptions={setUserRecordAccessSelectedOptions}
            />
          )}
        </div>
        <div className='mb-6 col-md-12 col-12'>
          {deptWiseAnalyticsLoading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <DepartmentWiseActivity
              chartData={deptWiseAnalytics}
              heading={'Department Wise Activity Over Time'}
              tooltip={
                "Monitor each department's document activity patterns, allowing you to identify which teams are most actively using the system."
              }
              selectedOptions={deptSelectedOptions}
              setSelectedOptions={setDeptSelectedOptions}
              graphType={'department'}
            />
          )}
        </div>
        <div className='mb-6 col-md-12 col-12'>
          {userWiseAnalyticsLoading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <DepartmentWiseActivity
              chartData={userWiseAnalytics}
              heading={'User Wise Activity Over Time'}
              tooltip={
                'Analyze individual user engagement with records over time. This visualization helps identify power users and detect unusual activity patterns.'
              }
              selectedOptions={userSelectedOptions}
              setSelectedOptions={setUserSelectedOptions}
              graphType={'user'}
            />
          )}
        </div>
        <div className='mb-6 col-md-6 col-12'>
          {analyticStatLoading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <RecordCountTime
              heading={formatMessage({ id: 'Record Count Over Time by Department' })}
              tooltip={formatMessage({
                id: 'Track how document volume grows within each department over time. This metric helps identify departments with increasing record management needs.',
              })}
              analyticStatData={analyticStatData}
              setFiles={setFiles}
              files={files}
            />
          )}
        </div>
        <div className='mb-6 col-md-6 col-12'>
          {analyticStatLoading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <RecordAccessGeographic
              title={'Record Access Geographic Distribution'}
              accessLocations={analyticStatData?.accessLocations as string[] | []}
            />
          )}
        </div>
        <div className='col-md-4 col-12 mb-6'>
          {loading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <div className='card rounded bg-white p-4'>
              <p className='text-muted'>{formatMessage({ id: 'Storage Utilization' })}</p>
              <div className='d-flex justify-content-center flex-column align-items-center'>
                <AnalyticsOverviewPieChartWidget
                  data={storageUtilizationData || []}
                  colors={recordsKeeperColors}
                  height={250}
                  width={400}
                />
              </div>
            </div>
          )}
        </div>

        <div className='col-md-4 col-12 mb-6'>
          {loading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <div className='card rounded bg-white p-4'>
              <p className='text-muted'>{formatMessage({ id: 'Departments Storage Consumed' })}</p>
              <div className='d-flex justify-content-center flex-column align-items-center'>
                <AnalyticsOverviewPieChartWidget
                  data={analyticStatData?.departmentPercentage || []}
                  colors={recordsKeeperColors}
                  width={400}
                  height={250}
                />
              </div>
            </div>
          )}
        </div>

        <div className='col-md-4 col-12 mb-6'>
          {loading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <div className='card rounded bg-white p-4'>
              <p className='text-muted'>{formatMessage({ id: 'Retention Period Status' })}</p>
              <div className='d-flex justify-content-center flex-column align-items-center'>
                <AnalyticsOverviewPieChartWidget
                  data={
                    analyticStatData?.retentionPercentageMap
                      ? analyticStatData?.retentionPercentageMap.map(
                        (retention: {
                          name: string
                          value: string
                          expirySoonCount: number
                          totalCount: number
                        }) => ({
                          name: retention.name,
                          value: parseInt(retention.value),
                        })
                      )
                      : []
                  }
                  colors={recordsKeeperColors}
                  width={400}
                  height={250}
                />
              </div>
            </div>
          )}
        </div>
        <div className='mb-6 col-md-6 col-12'>
          {analyticStatLoading ? (
            <div className='p-4 bg-white rounded-2'>
              <Skeleton
                height={300}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : (
            <FileCountGrowthGraph
              title='File Count Growth by Users'
              analyticStatData={analyticStatData}
              setUser={setUser}
              user={user}
            />
          )}
        </div>
        <div className='mb-6 col-md-6 col-12'>
          <CountFileType heading='Count by File Types' />
        </div>
        <div className='mb-6 col-md-6 col-12'>
          <RetentionPeriod
            retentionData={analyticStatData.recordWithMaxRetention as any[]}
            isLoading={loading}
          />
        </div>
        <div className='mb-6 col-md-6 col-12'>
          <QuickAction />
        </div>
      </div>
    </>
  )
}

export default AnalyticsOverview
